/**
 * Hardware hooks using React Relay
 *
 * These hooks provide access to hardware data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  IHardware,
  IHardwareType,
} from '../../../../ts/interface/hardware.interface';
import type {
  useHardwareRelayQuery as UseHardwareRelayQuery,
  useHardwareRelayQuery$variables,
  HardwareFilterInput,
  HardwareSortInput,
} from './__generated__/useHardwareRelayQuery.graphql';
import type { useHardwareRelayDirectQuery as UseHardwareRelayDirectQuery } from './__generated__/useHardwareRelayDirectQuery.graphql';
import type { useHardwareRelayByIdQuery } from './__generated__/useHardwareRelayByIdQuery.graphql';
import type { useHardwareRelayPaginationFragment$key } from './__generated__/useHardwareRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Fragment for hardware fields - reusable across queries
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _HardwareFragment = graphql`
  fragment useHardwareRelayFragment on Hardware {
    id
    oldId
    mac
    imei
    iccid
    label
    number
    state
    activeRentalId
    batteryPercent
    batteryVoltage
    charging
    firmwareVersion
    hardwareProductId
    hardwareProductOldId
    hardwareProductVersionId
    hardwareProductVersionOldId
    vendorId
    vendorQrCode
    vendorSerial
    locationId
    locationOldId
    organizationId
    systemId
    systemOldId
    lat
    lng
    lastSeen
    isAvailable
    isRentable
    isConnected
    isUnlocked
    hasOpenSafetyTicket
    createdOn
  }
`;

/**
 * Pagination fragment for hardware list
 */
const HardwareListPaginationFragment = graphql`
  fragment useHardwareRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useHardwareRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "HardwareFilterInput" }
    sort: { type: "[HardwareSortInput!]" }
  ) {
    listHardware(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useHardwareRelayPaginationFragment_listHardware"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          mac
          imei
          iccid
          label
          number
          state
          activeRentalId
          batteryPercent
          batteryVoltage
          charging
          firmwareVersion
          hardwareProductId
          hardwareProductOldId
          hardwareProductVersionId
          hardwareProductVersionOldId
          vendorId
          vendorQrCode
          vendorSerial
          locationId
          locationOldId
          organizationId
          systemId
          systemOldId
          lat
          lng
          lastSeen
          isAvailable
          isRentable
          isConnected
          isUnlocked
          createdOn
          # New fields from updated schema
          hardwareType {
            id
            name
            namePlural
          }
          location {
            id
            name
          }
          openMaintenanceTickets
          overdueMaintenanceTickets
          hasOpenSafetyTicket
          hasLowBattery
          isActive
          isInRental
          isOnline
          activeRental {
            id
            oldId
            startTime
            userId
          }
          qrCode {
            id
          }
          hardwareProduct {
            id
            primaryIdentifier
            supportsRemoteUnlock
            supportsRemoteLock
            supportsRemoteRing
          }
          system {
            id
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated hardware list
 */
const HardwareListQuery = graphql`
  query useHardwareRelayQuery(
    $first: Int
    $after: String
    $filter: HardwareFilterInput
    $sort: [HardwareSortInput!]
  ) {
    ...useHardwareRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for hardware list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const HardwareListDirectQuery = graphql`
  query useHardwareRelayDirectQuery(
    $first: Int
    $after: String
    $filter: HardwareFilterInput
    $sort: [HardwareSortInput!]
  ) {
    listHardware(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          mac
          imei
          iccid
          label
          number
          state
          activeRentalId
          batteryPercent
          batteryVoltage
          charging
          firmwareVersion
          hardwareProductId
          hardwareProductOldId
          hardwareProductVersionId
          hardwareProductVersionOldId
          vendorId
          vendorQrCode
          vendorSerial
          locationId
          locationOldId
          organizationId
          systemId
          systemOldId
          lat
          lng
          lastSeen
          isAvailable
          isRentable
          isConnected
          isUnlocked
          createdOn
          hardwareType {
            id
            name
            namePlural
          }
          location {
            id
            name
          }
          openMaintenanceTickets
          overdueMaintenanceTickets
          hasOpenSafetyTicket
          hasLowBattery
          isActive
          isInRental
          isOnline
          activeRental {
            id
            oldId
            startTime
            userId
          }
          qrCode {
            id
          }
          hardwareProduct {
            id
            primaryIdentifier
            supportsRemoteUnlock
            supportsRemoteLock
            supportsRemoteRing
          }
          system {
            id
            name
          }
        }
      }
    }
  }
`;

/**
 * Query for single hardware by ID
 */
const HardwareByIdQuery = graphql`
  query useHardwareRelayByIdQuery($id: ID!) {
    getHardware(id: $id) {
      ...useHardwareRelayFragment
      children {
        id
        oldId
        label
        number
        state
      }
      parent {
        id
        oldId
        label
        number
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { HardwareFilterInput, HardwareSortInput };

/**
 * Hardware type from GraphQL - matches the fragment fields
 */
export interface HardwareGraphQL {
  id: string;
  oldId?: string | null;
  mac?: string | null;
  imei?: string | null;
  iccid?: string | null;
  label?: string | null;
  number?: number | null;
  state?: number | null;
  activeRentalId?: string | null;
  batteryPercent?: number | null;
  batteryVoltage?: number | null;
  charging?: boolean | null;
  firmwareVersion?: string | null;
  hardwareProductId: string;
  hardwareProductOldId: string;
  hardwareProductVersionId?: string | null;
  hardwareProductVersionOldId?: string | null;
  vendorId?: string | null;
  vendorQrCode?: string | null;
  vendorSerial?: string | null;
  locationId?: string | null;
  locationOldId?: string | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
  lat?: number | null;
  lng?: number | null;
  lastSeen?: string | null;
  isAvailable?: boolean | null;
  isRentable?: boolean | null;
  isConnected?: boolean | null;
  isUnlocked?: boolean | null;
  createdOn: string;
  children?: HardwareGraphQL[];
  parent?: HardwareGraphQL;
  // New fields from updated schema
  hardwareType?: {
    id: string;
    name: string;
    namePlural?: string | null;
  } | null;
  location?: {
    id: string;
    name: string;
  } | null;
  openMaintenanceTickets?: number;
  overdueMaintenanceTickets?: number;
  hasOpenSafetyTicket?: boolean | null;
  hasLowBattery?: boolean | null;
  isActive?: boolean | null;
  isInRental?: boolean | null;
  isOnline?: boolean | null;
  activeRental?: {
    id: string;
    oldId?: string | null;
    startTime?: string | null;
    userId?: string | null;
  } | null;
  qrCode?: {
    id: string;
  } | null;
  hardwareProduct?: {
    id: string;
    primaryIdentifier?: string | null;
    supportsRemoteUnlock?: boolean | null;
    supportsRemoteLock?: boolean | null;
    supportsRemoteRing?: boolean | null;
  } | null;
  system?: {
    id: string;
    name?: string | null;
  } | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface HardwareQueryFilter {
  systemId?: string;
  systemOldId?: string;
  organizationId?: string;
  hardwareTypeId?: string;
  hardwareTypeName?: string[];
  hardwareProductId?: string | string[];
  hardwareProductVersionId?: string;
  vendorId?: string;
  vendorSerial?: string;
  locationId?: string;
  state?: number | number[];
  mac?: string;
  imei?: string;
  number?: number;
  label?: string;
  search?: string;
  isAvailable?: boolean;
  isOnline?: boolean;
  isInRental?: boolean;
}

// ============================================================================
// Sorting
// ============================================================================

/**
 * Table sort key -> the GraphQL sort fields it maps to. Shared by the system and
 * organization hardware lists so a column sorts identically in both.
 *
 * Battery maps to two fields so one click orders by percentage and then by
 * voltage: units that report only voltage all share a null percentage, and the
 * second key orders within that group instead of leaving it arbitrary.
 */
const SORT_KEY_TO_GQL_FIELDS: Record<string, HardwareSortInput['field'][]> = {
  created_on: ['CREATED_ON'],
  number: ['NUMBER'],
  state: ['STATE'],
  battery_percent: ['BATTERY_PERCENT', 'BATTERY_VOLTAGE'],
  label: ['LABEL'],
  open_tickets: ['OPEN_MAINTENANCE_TICKETS'],
  overdue_tickets: ['OVERDUE_MAINTENANCE_TICKETS'],
};

/**
 * Builds the GraphQL `sort` argument for a table sort key. Unknown keys fall
 * back to newest-first so a column without a server-side equivalent never sorts
 * silently by the wrong field.
 */
export function buildHardwareSort(
  key: string | undefined,
  direction: 'asc' | 'desc' | string | undefined
): HardwareSortInput[] {
  const fields = (key && SORT_KEY_TO_GQL_FIELDS[key]) || ['CREATED_ON'];
  const order = direction === 'asc' ? 'ASC' : 'DESC';
  return fields.map((field) => ({ field, order }));
}

/** Table sort keys that the server can actually sort by. */
export const SORTABLE_HARDWARE_KEYS = Object.keys(SORT_KEY_TO_GQL_FIELDS);

// ============================================================================
// Mappers
// ============================================================================

/**
 * Get primary identifier type based on available hardware data
 */
export function getPrimaryIdentifier(hw: HardwareGraphQL): 'number' | 'imei' | 'mac' {
  const productIdentifier = hw.hardwareProduct?.primaryIdentifier;
  if (productIdentifier) {
    return productIdentifier as 'number' | 'imei' | 'mac';
  }
  if (hw.number !== null && hw.number !== undefined) return 'number';
  if (hw.imei) return 'imei';
  if (hw.mac) return 'mac';
  return 'number';
}

/**
 * Maps GraphQL hardware data (from listHardware) to the shape expected by
 * hardwareColumnsV2 / the IHardware interface. Shared by the system and
 * organization hardware list pages so both render identically.
 */
export function mapHardwareToIHardware(hw: HardwareGraphQL): IHardware & {
  open_ticket_count: number;
  overdue_ticket_count: number;
  battery_voltage: number | null;
  vendor_serial: string | null;
  vendor_qr_code: string | null;
  qr_id: string | null;
  remote_connection_async: boolean | null;
  supports_remote_unlock: boolean;
  supports_remote_lock: boolean;
  supports_remote_ring: boolean;
  system_name: string;
  system?: { name: string };
  label: string | null;
} {
  const primaryIdentifier = getPrimaryIdentifier(hw);

  return {
    id: hw.id,
    old_id: hw.oldId,
    gid: hw.id,
    mac: hw.mac,
    imei: hw.imei,
    number: hw.number,
    label: hw.label,
    state: hw.state,
    battery_percent: hw.batteryPercent,
    battery_voltage: hw.batteryVoltage,
    latitude: hw.lat,
    longitude: hw.lng,
    last_lat: hw.lat,
    last_lng: hw.lng,
    vendor_id: hw.vendorId,
    vendor_serial: hw.vendorSerial ?? null,
    vendor_qr_code: hw.vendorQrCode ?? null,
    qr_id: hw.qrCode?.id ?? null,
    children: (hw.children ?? []) as unknown as IHardware[],
    parents: hw.parent ? [hw.parent as unknown as IHardware] : [],
    hardware_type_name: hw.hardwareType?.name ?? '',
    hardware_type: hw.hardwareType
      ? {
          id: hw.hardwareType.id,
          name: hw.hardwareType.name,
          label: hw.hardwareType.name,
          name_plural: hw.hardwareType.namePlural ?? `${hw.hardwareType.name}s`,
        }
      : ({} as IHardwareType),
    primary_identifier: primaryIdentifier,
    has_safety_ticket: hw.hasOpenSafetyTicket ?? false,
    current_station: hw.location?.name ?? '',
    current_station_id: hw.locationId ?? '',
    station: hw.location?.name ?? '',
    station_name: hw.location?.name ?? '',
    hardware_product_name: null,
    has_gps: false,
    remote_connection_async: hw.isOnline ?? null,
    supports_remote_unlock: hw.hardwareProduct?.supportsRemoteUnlock ?? false,
    supports_remote_lock: hw.hardwareProduct?.supportsRemoteLock ?? false,
    supports_remote_ring: hw.hardwareProduct?.supportsRemoteRing ?? false,
    type_name: hw.hardwareType?.name ?? '',
    hardwareTypes: hw.hardwareType ? [hw.hardwareType.id] : [],
    module: '',
    rentals: [],
    tickets: [],
    logs: [],
    display_name: hw.label ?? `#${hw.number}`,
    child_hardware_type_name: '',
    parent_hardware_type_name: '',
    system_name: hw.system?.name ?? '',
    system: hw.system ? { name: hw.system.name ?? '' } : undefined,
    current_rental: hw.activeRental
      ? {
          id: hw.activeRental.id,
          old_id: hw.activeRental.oldId ?? '',
          start_time: hw.activeRental.startTime ?? '',
          user_id: hw.activeRental.userId ?? '',
        }
      : undefined,
    open_ticket_count: hw.openMaintenanceTickets ?? 0,
    overdue_ticket_count: hw.overdueMaintenanceTickets ?? 0,
    // Preserve null (distinct from false) so the Available and In Rental
    // columns can show N/A.
    is_available: hw.isAvailable ?? null,
    is_rentable: hw.isRentable ?? null,
    is_in_rental: hw.isInRental ?? null,
  };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function HardwareList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useHardwareRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(hardware => <div key={hardware.id}>{hardware.label}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <HardwareList />
 * </Suspense>
 */
export function useHardwareRelay(
  filter?: HardwareQueryFilter,
  options?: {
    limit?: number;
    sort?: HardwareSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useHardwareRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseHardwareRelayQuery>(HardwareListQuery, variables);

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseHardwareRelayQuery,
    useHardwareRelayPaginationFragment$key
  >(HardwareListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes = data.listHardware?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes,
    count: data.listHardware?.count,
    pageInfo: data.listHardware?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single hardware item by ID using Relay
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareDetail({ id }: { id: string }) {
 *   const hardware = useHardwareByIdRelay(id);
 *   return <div>{hardware?.label}</div>;
 * }
 */
export function useHardwareByIdRelay(hardwareId: string) {
  const data = useLazyLoadQuery<useHardwareRelayByIdQuery>(HardwareByIdQuery, {
    id: hardwareId,
  });

  return data.getHardware;
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for hardware list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 *
 * @example
 * function HardwareContainer() {
 *   const { queryRef, loadQuery, isLoading } = useHardwareRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery({ systemId: 'uuid' });
 *   }, []);
 *
 *   if (isLoading || !queryRef) {
 *     return <Loading />;
 *   }
 *
 *   return <HardwareListWithData queryRef={queryRef} />;
 * }
 *
 * function HardwareListWithData({ queryRef }) {
 *   const data = useHardwareRelayPreloaded(queryRef);
 *   return <div>{data.map(h => h.label)}</div>;
 * }
 */
export function useHardwareRelayLoader(initialFilter?: HardwareQueryFilter) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseHardwareRelayQuery>(
    HardwareListQuery,
    initialFilter
      ? {
          first: 100,
          filter: buildRelayFilter(initialFilter),
          sort: [{ field: 'CREATED_ON', order: 'DESC' }],
        }
      : undefined
  );

  const load = (
    filter?: HardwareQueryFilter,
    options?: { limit?: number; sort?: HardwareSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

    loadQuery({
      first: limit,
      filter: buildRelayFilter(filter),
      sort,
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded hardware query
 *
 * Use with useHardwareRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useHardwareRelayPreloaded(
  queryRef: PreloadedQuery<UseHardwareRelayQuery>
) {
  const queryData = usePreloadedQuery<UseHardwareRelayQuery>(HardwareListQuery, queryRef);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseHardwareRelayQuery,
    useHardwareRelayPaginationFragment$key
  >(HardwareListPaginationFragment, queryData);

  const nodes = data.listHardware?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes,
    count: data.listHardware?.count,
    pageInfo: data.listHardware?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for hardware by ID with explicit loading state (no Suspense required)
 *
 * @example
 * function HardwareDetail({ id }: { id: string }) {
 *   const { queryRef, loadQuery, isLoading } = useHardwareByIdRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery(id);
 *   }, [id]);
 *
 *   if (isLoading || !queryRef) return <Loading />;
 *
 *   return <HardwareDetailContent queryRef={queryRef} />;
 * }
 */
export function useHardwareByIdRelayLoader(initialId?: string) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<useHardwareRelayByIdQuery>(
    HardwareByIdQuery,
    initialId ? { id: initialId } : undefined
  );

  const load = (hardwareId: string) => {
    loadQuery({ id: hardwareId });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded hardware-by-id query
 */
export function useHardwareByIdRelayPreloaded(
  queryRef: PreloadedQuery<useHardwareRelayByIdQuery>
) {
  const data = usePreloadedQuery<useHardwareRelayByIdQuery>(HardwareByIdQuery, queryRef);
  return data.getHardware;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL HardwareFilterInput from the simplified filter interface
 */
function buildRelayFilter(filter?: HardwareQueryFilter): HardwareFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: HardwareFilterInput = {};

  if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.hardwareTypeId) {
    relayFilter.hardwareType = { id: { eq: filter.hardwareTypeId } };
  }
  if (filter.hardwareTypeName && filter.hardwareTypeName.length > 0) {
    relayFilter.hardwareType = {
      ...relayFilter.hardwareType,
      name: { in: filter.hardwareTypeName },
    };
  }
  if (Array.isArray(filter.hardwareProductId)) {
    if (filter.hardwareProductId.length > 0) {
      relayFilter.hardwareProductId = { in: filter.hardwareProductId };
    }
  } else if (filter.hardwareProductId) {
    relayFilter.hardwareProductId = { eq: filter.hardwareProductId };
  }
  if (filter.hardwareProductVersionId) {
    relayFilter.hardwareProductVersionId = { eq: filter.hardwareProductVersionId };
  }
  if (filter.vendorId) {
    relayFilter.vendorId = { eq: filter.vendorId };
  }
  if (filter.vendorSerial) {
    relayFilter.vendorSerial = { eq: filter.vendorSerial };
  }
  if (filter.locationId) {
    relayFilter.locationId = { eq: filter.locationId };
  }
  if (filter.state !== undefined) {
    if (Array.isArray(filter.state)) {
      relayFilter.state = { in: filter.state };
    } else {
      relayFilter.state = { eq: filter.state };
    }
  }
  if (filter.mac) {
    relayFilter.mac = { eq: filter.mac };
  }
  if (filter.imei) {
    relayFilter.imei = { eq: filter.imei };
  }
  if (filter.number !== undefined) {
    relayFilter.number = { eq: filter.number };
  }
  if (filter.label) {
    // Support pattern matching for label - if it contains % use ilike, otherwise eq
    if (filter.label.includes('%')) {
      relayFilter.label = { ilike: filter.label };
    } else {
      relayFilter.label = { eq: filter.label };
    }
  }
  if (filter.isAvailable !== undefined) {
    relayFilter.isAvailable = { eq: filter.isAvailable };
  }
  if (filter.isOnline !== undefined) {
    relayFilter.isOnline = { eq: filter.isOnline };
  }
  if (filter.isInRental !== undefined) {
    relayFilter.isInRental = { eq: filter.isInRental };
    // The server currently reports isInRental: false for non-rentable units, which
    // the In Rental column renders as "N/A" — so an unqualified `eq: false` returns
    // rows the column says the filter shouldn't have matched. Restricting to
    // rentable units makes the list agree with what it displays. Redundant once ant
    // returns nil for non-rentable units (movatic-mobility/ant#26), but harmless.
    relayFilter.isRentable = { eq: true };
  }
  if (filter.search) {
    const searchPattern = `%${filter.search}%`;
    relayFilter.or = [
      { label: { ilike: searchPattern } },
      { imei: { ilike: searchPattern } },
      { mac: { ilike: searchPattern } },
      { numberText: { ilike: searchPattern } },
      { vendorQrCode: { ilike: searchPattern } },
    ];
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

// Type for the raw response from fetchQuery
type HardwareListQueryResponse = {
  listHardware: {
    count?: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
    edges?: Array<{
      cursor: string;
      node: HardwareGraphQL;
    }>;
  };
};

/**
 * Hook that provides React Query-like interface for hardware data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched } = useHardwareRelayQuery({
 *   systemId: 'uuid',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(h => h.label)}</div>;
 */
export function useHardwareRelayQuery(
  filter?: HardwareQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: HardwareSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<HardwareGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const builtFilter = buildRelayFilter(filter);
        const variables: useHardwareRelayQuery$variables = {
          first: limit,
          after,
          sort,
          ...(builtFilter ? { filter: builtFilter } : {}),
        };

        const result = await fetchQuery<UseHardwareRelayDirectQuery>(
          relayEnvironment,
          HardwareListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const response = result as unknown as HardwareListQueryResponse;
          const nodes =
            response.listHardware?.edges?.map((edge) => edge.node).filter(Boolean) ?? [];
          setData(nodes);
          setCount(response.listHardware?.count);
          setHasMore(response.listHardware?.pageInfo?.hasNextPage ?? false);
          setEndCursor(response.listHardware?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch hardware'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
